import { Component, Inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { filter, first, interval, map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { eBusinessSearchCriteriaType, eBusinessSearchScope, eOrderStatusType, eOrderType } from '../../enums';
import { OrderStateRepositoryService } from '../../services/order-state/order-state-repository.service';
import { BusinessSearchResultRepositoryService } from '../../services/business-search-results/business-search-result-repository.service';
import { BusinessSearchResult } from '../../interfaces/business-search-results/business-search-result';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { ListComponent } from '../list-component';
import { HttpParams } from '@angular/common/http';
import { Order } from '../../interfaces/orders/order';
import { TranslocoService } from '@jsverse/transloco';

export interface ValidateBusinessEntityComponentLabels {
  title: string;
  nameColumnHeader: string;
  numberColumnHeader: string;
  jurisdictionColumnHeader: string;
  statusColumnHeader: string;
  noResultsFilters: string;
  noResults: string;
  suggestedMatch: string;
  results: string;
  otherResults: string;
  buttons: {
    select: string;
    close: string;
    filters: string;
    filter: string;
    clearFilters: string;
  }
}

@Component({
    selector: 'lib-validate-business-entity',
    templateUrl: './validate-business-entity.component.html',
    styleUrls: ['./validate-business-entity.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ValidateBusinessEntityComponent extends ListComponent<BusinessSearchResult> implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  protected spinning = true;
  protected hasResults = false;
  protected businessSearchOrderID: string | undefined;
  
  protected name: string | undefined;
  protected jurisdiction!: string;
  protected orderGroupID: string | undefined;
  
  protected order?: Order;
  protected results: BusinessSearchResult[] = [];
  protected selectedResult: BusinessSearchResult | undefined;
  protected suggestedResult: BusinessSearchResult | undefined;
  protected displayedColumns: string[] = ['name', 'number', 'jurisdiction', 'status'];

  private onDestroy$ = new Subject<void>();

  private _labels?: Partial<ValidateBusinessEntityComponentLabels>;
  @Input() set labels(value: Partial<ValidateBusinessEntityComponentLabels> | null | undefined) { this._labels = value ?? undefined; }
  /** Caller-provided labels override the translated defaults. */
  get labels(): ValidateBusinessEntityComponentLabels {
    const defaults: ValidateBusinessEntityComponentLabels = {
      title: this.transloco.translate('validateBusinessEntity.title'),
      nameColumnHeader: this.transloco.translate('common.labels.name'),
      numberColumnHeader: this.transloco.translate('common.labels.number'),
      jurisdictionColumnHeader: this.transloco.translate('common.labels.jurisdiction'),
      statusColumnHeader: this.transloco.translate('common.labels.status'),
      noResultsFilters: this.transloco.translate('validateBusinessEntity.noResultsFilters'),
      noResults: this.transloco.translate('validateBusinessEntity.noResults'),
      suggestedMatch: this.transloco.translate('validateBusinessEntity.suggestedMatch'),
      results: this.transloco.translate('validateBusinessEntity.results'),
      otherResults: this.transloco.translate('validateBusinessEntity.otherResults'),
      buttons: { select: this.transloco.translate('validateBusinessEntity.select'), close: this.transloco.translate('common.buttons.close'), filters: this.transloco.translate('common.labels.filters'), filter: this.transloco.translate('common.labels.filter'), clearFilters: this.transloco.translate('common.labels.clearFilters') },
    };
    return { ...defaults, ...this._labels, buttons: { ...defaults.buttons, ...this._labels?.buttons } } as ValidateBusinessEntityComponentLabels;
  }

  constructor(
    private ordersRepo: OrderRepositoryService,
    private orderStateRepo: OrderStateRepositoryService,
    businessSearchResultsRepository: BusinessSearchResultRepositoryService,
    @Inject(MAT_DIALOG_DATA) public data: { name: string, jurisdiction: string, orderGroupID: string, labels: ValidateBusinessEntityComponentLabels }) {
      super(businessSearchResultsRepository);
      this.name = data.name;
      this.jurisdiction = data.jurisdiction;
      this.orderGroupID = data.orderGroupID;
      this.labels = data.labels;
  }
  
  ngOnInit(): void {
    if(!this.name) {
      return;
    }
    
    // Have we already searched for this business name?
    this.ordersRepo.getOrders({
      orderGroupID: this.orderGroupID,
      businessName: this.name,
      orderTypes: eOrderType.businessSearch,
      status: eOrderStatusType.complete
    }).pipe(
      takeUntil(this.onDestroy$),
      // Mapping observable into another order - existing or new
      switchMap(orders => {
        if(orders.length > 0) {
          // If so, we can just return results for that order
          this.order= orders.find(o => o.businessSearchCriteria?.name?.toLowerCase() === this.name?.toLowerCase()) ?? orders.at(0);
          this.businessSearchOrderID = this.order!.id!
          this.repo.populateBaseLink([this.businessSearchOrderID]);
          
          // Since the other option returns the status of the new order, we return the status of the old one here to match up types
          return of({ status: eOrderStatusType.complete });
        } else {
          // If not, we need to order a new one
          return this.ordersRepo.createDraftOrder({
            referenceNumber: `Attestor-Bus-Srch`,
            country: 'CA',
            jurisdiction: '_M',
            orderTypeID: eOrderType.businessSearch,
            orderGroupID: this.orderGroupID,
            businessSearchCriteria: {
              businessSearchCriteriaTypeID: eBusinessSearchCriteriaType.Name,
              businessSearchScopeID: eBusinessSearchScope.Fast,
              name: this.name,
            },
            searchParameters: {
              searchJurisdictions: [
                { jurisdiction: 'BC' },
                { jurisdiction: 'AB' },
                { jurisdiction: 'SK' },
                { jurisdiction: 'MB' },
                { jurisdiction: 'ON' },
                { jurisdiction: 'QC' },
                { jurisdiction: 'NL' },
                { jurisdiction: 'NB' },
                { jurisdiction: 'NS' },
                { jurisdiction: 'PE' },
                { jurisdiction: 'YT' },
                { jurisdiction: 'NT' },
                { jurisdiction: 'NU' },
                { jurisdiction: '_F' }
              ]
            }
          }).pipe(
            filter(response => !!response.resource && response.validation.errors!.length === 0),
            tap(response => this.order = response.resource),
            map(response => response.resource.id),
            tap(id => {
              this.businessSearchOrderID = id
              this.repo.populateBaseLink([id]);
            }),
            switchMap(id => this.orderStateRepo.updateOrderState(id, { newStatus: eOrderStatusType.pending, message: 'Submitted automatically for attestor validation.'})),
            switchMap(() => interval(750).pipe(
              switchMap(() => this.orderStateRepo.getOrderState(this.businessSearchOrderID!)),
              first(status => status.status === eOrderStatusType.complete)
            ))
          )
        }
      }),
      // Mapping the previous work into getting the results
      switchMap(() => {
        this.order!.orderStatusTypeID = eOrderStatusType.complete;

        let defaultParams = new HttpParams()
          .set('sortColumn', 'score')
          .set('sortDirection', 'asc')
          .set('showResult', true);
          
        return this.repo.getFirstPageSubscribable(defaultParams);
      })
    ).subscribe(results => {
      this.hasResults = results.length > 0;
      this.spinning = false;
      const suggested =
        results.find(res => res.score === 0 && !!this.jurisdiction && res.jurisdiction === this.jurisdiction) ??
        results.find(res => res.score === 0) ??
        results.reduce((best: BusinessSearchResult | undefined, r) =>
          (r.score ?? Infinity) < (best?.score ?? Infinity) ? r : best, undefined);

      if (suggested) {
        this.suggestedResult = suggested;
        this.selectedResult = suggested;
      }
    });
  }
  
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
  
  getJurisdiction(result: BusinessSearchResult) {
    var jurisdiction = result.jurisdiction;
    if (result.jurisdiction == "_F") {
      jurisdiction = "FED";
    }

    if (result.isExtraJurisdictional) {
      let homeJurisdiction = result.homeJurisdiction ? result.homeJurisdiction : 'unknown';
      jurisdiction = `${result.jurisdiction} (EP - ${homeJurisdiction != "_F" ? homeJurisdiction : "FED"})`;
    }

    return jurisdiction;
  }
  
  isGroupHeader = (_: number, row: any): boolean => !!row.isHeader;

  buildTableRows(results: BusinessSearchResult[]): ({ isHeader: true; label: string } | BusinessSearchResult)[] {
    const rows: ({ isHeader: true; label: string } | BusinessSearchResult)[] = [];
    if (this.suggestedResult) {
      rows.push({ isHeader: true, label: this.labels.suggestedMatch });
      rows.push(this.suggestedResult);
    }
    const others = results.filter(r => r !== this.suggestedResult);
    if (others.length > 0) {
      rows.push({ isHeader: true, label: this.suggestedResult ? this.labels.otherResults : this.labels.results});
      rows.push(...others);
    }
    return rows;
  }

  onRowClick(result: BusinessSearchResult) {
    if (this.selectedResult === result) {
      this.selectedResult = undefined;
    } else {
      this.selectedResult = result;
    }
  }
}
